import React, { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { httpsCallable } from "firebase/functions";
import { jsPDF } from "jspdf";
import { FileDown, FileText, Upload, CheckCircle2, XCircle, Loader2, AlertTriangle } from "lucide-react";
import { functions } from "../firebase/config";
import { Logo } from "../components/Logo";

const getAccountantDataFn = httpsCallable(functions, "getAccountantData");
const saveAccountantUploadFn = httpsCallable(functions, "saveAccountantUpload");

const BOM = "﻿";

function formatCurrency(value) {
  return new Intl.NumberFormat("it-IT", {
    style: "currency",
    currency: "EUR",
    minimumFractionDigits: 2,
  }).format(value || 0);
}

function formatDateIT(isoDate) {
  if (!isoDate) return "—";
  const parts = String(isoDate).split("-");
  if (parts.length !== 3) return isoDate;
  const [year, month, day] = parts;
  return `${day}/${month}/${year}`;
}

function downloadBlob(blob, fileName) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = fileName;
  a.click();
  URL.revokeObjectURL(url);
}

function fileToBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      // Il risultato è "data:<mime>;base64,<dati>" -> teniamo solo la parte dopo la virgola.
      const result = String(reader.result || "");
      const commaIdx = result.indexOf(",");
      resolve(commaIdx >= 0 ? result.slice(commaIdx + 1) : result);
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

export default function AccountantPortalPage() {
  const { uid, token } = useParams();

  const [status, setStatus] = useState("loading"); // loading | ok | error
  const [data, setData] = useState(null);
  const [errorMsg, setErrorMsg] = useState("");

  const [yearFilter, setYearFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all"); // all | paid | unpaid

  const [uploadType, setUploadType] = useState("f24");
  const [uploadState, setUploadState] = useState("idle"); // idle | uploading | success | error
  const [uploadError, setUploadError] = useState("");

  useEffect(() => {
    let cancelled = false;

    (async () => {
      if (!uid || !token) {
        setStatus("error");
        setErrorMsg("Link non valido.");
        return;
      }
      try {
        const res = await getAccountantDataFn({ uid, token });
        if (!cancelled) {
          setData(res.data);
          setStatus("ok");
        }
      } catch (err) {
        console.error("Errore caricamento dati commercialista:", err);
        if (!cancelled) {
          setStatus("error");
          setErrorMsg(
            err?.code === "functions/permission-denied" || err?.code === "permission-denied"
              ? "Link non valido o scaduto."
              : "Impossibile caricare i dati. Riprova più tardi."
          );
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [uid, token]);

  const years = useMemo(() => {
    if (!data?.invoices) return [];
    const set = new Set(
      data.invoices
        .map((inv) => (inv.issueDate ? new Date(inv.issueDate).getFullYear() : null))
        .filter(Boolean)
    );
    return Array.from(set).sort((a, b) => b - a);
  }, [data]);

  const filteredInvoices = useMemo(() => {
    if (!data?.invoices) return [];
    return data.invoices.filter((inv) => {
      if (yearFilter !== "all") {
        const y = inv.issueDate ? new Date(inv.issueDate).getFullYear() : null;
        if (String(y) !== String(yearFilter)) return false;
      }
      if (statusFilter === "paid" && !inv.paid) return false;
      if (statusFilter === "unpaid" && inv.paid) return false;
      return true;
    });
  }, [data, yearFilter, statusFilter]);

  const filteredTotals = useMemo(() => {
    return filteredInvoices.reduce(
      (acc, inv) => {
        const amt = Number(inv.grossAmount) || 0;
        acc.totaleFatturato += amt;
        if (inv.paid) acc.totaleIncassato += amt;
        else acc.totaleDaIncassare += amt;
        return acc;
      },
      { totaleFatturato: 0, totaleIncassato: 0, totaleDaIncassare: 0 }
    );
  }, [filteredInvoices]);

  const titolareNome = [data?.titolare?.firstName, data?.titolare?.lastName].filter(Boolean).join(" ");

  const handleDownloadCsv = () => {
    const header = "Data emissione;Numero;Cliente;Importo lordo;Incassata;Data incasso;Scadenza";
    const rows = filteredInvoices.map((inv) => {
      const amt = (Number(inv.grossAmount) || 0).toFixed(2).replace(".", ",");
      return [
        formatDateIT(inv.issueDate),
        inv.invoiceCode || "",
        (inv.clientName || "").replace(/;/g, ","),
        amt,
        inv.paid ? "Sì" : "No",
        inv.paid ? formatDateIT(inv.paymentDate) : "",
        formatDateIT(inv.dueDate),
      ].join(";");
    });
    const csv = BOM + [header, ...rows].join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    downloadBlob(blob, `fatture_${titolareNome || uid}.csv`);
  };

  const handleDownloadInvoicePdf = (inv) => {
    const doc = new jsPDF();
    let y = 20;
    doc.setFontSize(16);
    doc.text("Dettaglio Fattura", 20, y);
    y += 12;
    doc.setFontSize(10);
    const rows = [
      ["Numero:", inv.invoiceCode || "—"],
      ["Cliente:", inv.clientName || "—"],
      ["Data emissione:", formatDateIT(inv.issueDate)],
      ["Scadenza:", formatDateIT(inv.dueDate)],
      ["Importo lordo:", formatCurrency(inv.grossAmount)],
      ["Incassata:", inv.paid ? "Sì" : "No"],
      ["Data incasso:", inv.paid ? formatDateIT(inv.paymentDate) : "—"],
    ];
    rows.forEach(([label, value]) => {
      doc.text(label, 20, y);
      doc.text(String(value), 90, y);
      y += 8;
    });
    doc.save(`fattura_${inv.invoiceCode || "export"}.pdf`);
  };

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploadState("uploading");
    setUploadError("");

    try {
      if (file.size > 5 * 1024 * 1024) {
        throw new Error("File troppo grande. Massimo 5MB.");
      }
      const fileDataBase64 = await fileToBase64(file);
      await saveAccountantUploadFn({
        uid,
        token,
        type: uploadType,
        fileName: file.name,
        fileDataBase64,
      });
      setUploadState("success");
    } catch (err) {
      console.error("Errore upload:", err);
      setUploadState("error");
      setUploadError(err?.message || "Errore durante il caricamento. Riprova.");
    } finally {
      e.target.value = "";
    }
  };

  if (status === "loading") {
    return (
      <div className="min-h-screen flex items-center justify-center bg-neutral-50">
        <div className="flex flex-col items-center gap-3 text-neutral-500">
          <Loader2 className="w-8 h-8 animate-spin text-forfy-500" />
          <span className="text-sm">Caricamento dati...</span>
        </div>
      </div>
    );
  }

  if (status === "error") {
    return (
      <div className="min-h-screen flex items-center justify-center bg-neutral-50 p-4">
        <div className="bg-white rounded-xl shadow-lg p-8 max-w-md w-full text-center">
          <XCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
          <h1 className="text-lg font-semibold text-neutral-900 mb-2">Link non valido o scaduto</h1>
          <p className="text-sm text-neutral-600">
            {errorMsg || "Il link non è più valido. Chiedi al tuo cliente di generarne uno nuovo."}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-neutral-50 text-neutral-900">
      {/* Header */}
      <header className="bg-white border-b border-neutral-200 sticky top-0 z-10">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
          <Logo variant="full" size="sm" />
          <div className="text-right">
            <div className="text-xs text-neutral-500">Portale Commercialista</div>
            {titolareNome && <div className="text-sm font-medium text-neutral-900">{titolareNome}</div>}
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 sm:px-6 py-6 pb-16">
        {/* Totali */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
          <div className="bg-white rounded-xl border border-neutral-200 p-4 shadow-sm">
            <div className="text-xs text-neutral-500">Fatturato totale</div>
            <div className="text-lg font-semibold mt-1">{formatCurrency(filteredTotals.totaleFatturato)}</div>
          </div>
          <div className="bg-white rounded-xl border border-neutral-200 p-4 shadow-sm">
            <div className="text-xs text-neutral-500">Incassato</div>
            <div className="text-lg font-semibold mt-1 text-green-600">{formatCurrency(filteredTotals.totaleIncassato)}</div>
          </div>
          <div className="bg-white rounded-xl border border-neutral-200 p-4 shadow-sm">
            <div className="text-xs text-neutral-500">Da incassare</div>
            <div className="text-lg font-semibold mt-1 text-forfy-600">{formatCurrency(filteredTotals.totaleDaIncassare)}</div>
          </div>
        </div>

        {/* Filtri + azioni */}
        <div className="bg-white rounded-xl border border-neutral-200 p-4 shadow-sm mb-6">
          <div className="flex flex-wrap items-center gap-3 justify-between">
            <div className="flex flex-wrap items-center gap-2">
              <select
                value={yearFilter}
                onChange={(e) => setYearFilter(e.target.value)}
                className="px-3 py-1.5 border border-neutral-200 rounded-lg text-sm bg-white"
              >
                <option value="all">Tutti gli anni</option>
                {years.map((y) => (
                  <option key={y} value={y}>{y}</option>
                ))}
              </select>

              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="px-3 py-1.5 border border-neutral-200 rounded-lg text-sm bg-white"
              >
                <option value="all">Tutte</option>
                <option value="paid">Incassate</option>
                <option value="unpaid">Da incassare</option>
              </select>
            </div>

            <button
              type="button"
              onClick={handleDownloadCsv}
              disabled={filteredInvoices.length === 0}
              className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-forfy-500 text-white text-sm hover:bg-forfy-600 disabled:opacity-50"
            >
              <FileDown className="w-4 h-4" />
              Scarica CSV
            </button>
          </div>
        </div>

        {/* Lista fatture */}
        <div className="bg-white rounded-xl border border-neutral-200 shadow-sm overflow-hidden mb-6">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-neutral-50 border-b border-neutral-200">
                <tr>
                  <th className="text-left px-4 py-2 font-medium text-neutral-600">Numero</th>
                  <th className="text-left px-4 py-2 font-medium text-neutral-600">Cliente</th>
                  <th className="text-left px-4 py-2 font-medium text-neutral-600">Emissione</th>
                  <th className="text-left px-4 py-2 font-medium text-neutral-600">Scadenza</th>
                  <th className="text-right px-4 py-2 font-medium text-neutral-600">Importo</th>
                  <th className="text-center px-4 py-2 font-medium text-neutral-600">Stato</th>
                  <th className="text-center px-4 py-2 font-medium text-neutral-600">PDF</th>
                </tr>
              </thead>
              <tbody>
                {filteredInvoices.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="px-4 py-8 text-center text-neutral-400">
                      Nessuna fattura trovata.
                    </td>
                  </tr>
                ) : (
                  filteredInvoices.map((inv, idx) => (
                    <tr key={idx} className="border-b border-neutral-100 last:border-0">
                      <td className="px-4 py-2 font-mono text-xs">{inv.invoiceCode || "—"}</td>
                      <td className="px-4 py-2">{inv.clientName || "—"}</td>
                      <td className="px-4 py-2">{formatDateIT(inv.issueDate)}</td>
                      <td className="px-4 py-2">{formatDateIT(inv.dueDate)}</td>
                      <td className="px-4 py-2 text-right">{formatCurrency(inv.grossAmount)}</td>
                      <td className="px-4 py-2 text-center">
                        {inv.paid ? (
                          <span className="inline-flex items-center gap-1 text-green-600 text-xs">
                            <CheckCircle2 className="w-3.5 h-3.5" /> Incassata
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 text-amber-600 text-xs">
                            <AlertTriangle className="w-3.5 h-3.5" /> Da incassare
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-2 text-center">
                        <button
                          type="button"
                          onClick={() => handleDownloadInvoicePdf(inv)}
                          className="text-forfy-600 hover:text-forfy-700"
                          aria-label={`Scarica PDF fattura ${inv.invoiceCode}`}
                        >
                          <FileText className="w-4 h-4" />
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Upload F24 / Dichiarazione */}
        <div className="bg-white rounded-xl border border-neutral-200 shadow-sm p-4">
          <h2 className="text-sm font-medium text-neutral-700 mb-1">Carica documenti</h2>
          <p className="text-xs text-neutral-500 mb-4">
            Carica qui F24 o dichiarazioni: saranno visibili al tuo cliente all'interno di Forfy.
          </p>

          <div className="flex flex-wrap items-center gap-3">
            <select
              value={uploadType}
              onChange={(e) => setUploadType(e.target.value)}
              className="px-3 py-1.5 border border-neutral-200 rounded-lg text-sm bg-white"
            >
              <option value="f24">F24</option>
              <option value="dichiarazione">Dichiarazione</option>
            </select>

            <label className="flex items-center gap-2 px-3 py-1.5 rounded-lg border border-neutral-200 bg-white text-sm text-neutral-700 hover:bg-neutral-50 cursor-pointer">
              <Upload className="w-4 h-4" />
              {uploadState === "uploading" ? "Caricamento..." : "Scegli file"}
              <input
                type="file"
                accept=".pdf,.p7m,.xml,.jpg,.jpeg,.png"
                onChange={handleUpload}
                disabled={uploadState === "uploading"}
                className="hidden"
              />
            </label>

            {uploadState === "success" && (
              <span className="inline-flex items-center gap-1 text-green-600 text-xs">
                <CheckCircle2 className="w-3.5 h-3.5" /> Caricato con successo
              </span>
            )}
            {uploadState === "error" && (
              <span className="inline-flex items-center gap-1 text-red-600 text-xs">
                <XCircle className="w-3.5 h-3.5" /> {uploadError}
              </span>
            )}
          </div>
        </div>
      </main>
    </div>
  );
}
